import cloneDeep from "lodash/cloneDeep";
import yaml from "yaml";
import { Node as YAMLNode, YAMLMap } from "yaml/types";
import { findPair, Type as YAMLType } from "yaml/util";

import { ALLOWED_VISUAL_PATTERN_KEYS } from "@constants";
import { ALL_VALID_LANGUAGE_KEYS, ID_BY_KEY, Language } from "@utils";

import {
  PatternPath,
  Position,
  RuleValidationError,
  SimpleRule,
} from "@pages/editor/types";

// Regex for validating Rule IDs, similar to [\w.-]+ on backend, but in different format because \w doesn't match non-latin characters in JS
const ID_REGEX = /^[\p{L}0-9_.-]+$/u;

export function ruleFromYAML(
  pattern: string,
  oldRule: SimpleRule,
  setLanguage?: Language
): [rule: SimpleRule | undefined, lossless: boolean] {
  /* Catch all parse errors and just circumvent the
    Simple Editor if yaml cannot be parsed
  */
  try {
    return protectedRuleFromYAML(pattern, oldRule, setLanguage);
  } catch (e) {
    console.warn(
      `while parsing: ${pattern}\nexception: unknown rule parse exception: ${e}`
    );
    return [undefined, false];
  }
}

function protectedRuleFromYAML(
  pattern: string,
  oldRule: SimpleRule,
  setLanguage?: Language
): [rule: SimpleRule | undefined, lossless: boolean] {
  /*
    Use automatic parser to convert YAML to dictionary
    but then restructure dict to fit our Rule object
  */
  const initPath = ["rules", 0];
  let parsedPattern = yaml.parse(pattern);
  removeEmptyKeys(parsedPattern);
  let rule = parsedPattern["rules"][0];
  let [lossLess, lossLessTemp] = [true, true];
  let yamlrule = assignNonPatternKeys(rule, oldRule);
  yamlrule.patterns = [];
  if (pattern.match(/^\s*?#/m)) {
    console.error(`not lossless: YAML string contains comments`);
    lossLess = false;
  }
  if (hasObjectValue(rule, "patterns")) {
    [yamlrule, lossLessTemp] = parsePatterns(yamlrule, rule, initPath);
    if (!lossLessTemp) {
      console.error(`not lossless: failed to parse "patterns" in ${yamlrule}`);
    }
    lossLess = lossLess && lossLessTemp;
  }
  if (hasObjectValue(rule, "pattern-either")) {
    [yamlrule, lossLessTemp] = parsePatternEither(yamlrule, rule, initPath);
    if (!lossLessTemp) {
      console.error(
        `not lossless: failed to parse "pattern-either" in ${yamlrule}`
      );
    }
    lossLess = lossLess && lossLessTemp;
  }
  if (hasStringValue(rule, "pattern")) {
    // This should not overwrite pattern-either patterns
    [yamlrule, lossLessTemp] = parsePattern(yamlrule, rule, initPath);
    if (!lossLessTemp) {
      console.error(`not lossless: failed to parse "pattern" in ${yamlrule}`);
    }
    lossLess = lossLess && lossLessTemp;
  }
  if (yamlrule.patterns.length === 0) {
    yamlrule.patterns = [
      {
        pattern: "",
        path: [...initPath, "pattern"],
        type: "pattern" as ALLOWED_VISUAL_PATTERN_KEYS,
      },
    ];
  }

  if (parsedPattern["rules"].length > 1) {
    // We only parse the first rule
    console.error(
      `not lossless: only support 1 rule but had ${parsedPattern["rules"].length}`
    );
    lossLess = false;
  }
  if (!Object.keys(rule).every(isSupportedKey)) {
    // Warn user before dropping unsupported keys from representation
    console.error(
      `not lossless: at least one unsupported rule key in ${Object.keys(rule)}`
    );
    lossLess = false;
  }
  yamlrule.patterns.sort((a: any, b: any) => (a.type < b.type ? -1 : 1));
  if (yamlrule.patterns[0].type !== "pattern") {
    // We assume that the first pattern in Simple editor has type "pattern"
    console.error(
      `not lossless: assumed root key would be of type pattern but found ${yamlrule.patterns[0].type}`
    );
    lossLess = false;
  }

  if (setLanguage !== undefined) {
    yamlrule.languages = [setLanguage];
  }
  return [yamlrule, lossLess];
}

function hasStringValue(dict: any, key: string) {
  return dict[key] && typeof dict[key] === "string";
}

function hasObjectValue(dict: any, key: string) {
  return dict[key] && typeof dict[key] === "object";
}

function removeEmptyKeys(obj: any) {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === "object") {
      removeEmptyKeys(obj[key]); // recurse
    } else if (obj[key] == null) {
      obj[key] = " "; // delete
    } else if (typeof obj[key] === "string") {
      obj[key] = obj[key].trim();
    }
  });
}

function isSupportedKey(key: string) {
  return [
    "rules",
    "id",
    "message",
    "fix",
    "severity",
    "patterns",
    "pattern-either",
    "pattern",
    "language",
    "languages",
    "metadata",
  ].includes(key);
}

function assignNonPatternKeys(newRule: any, oldRule: SimpleRule) {
  let yamlrule = cloneDeep(oldRule);
  if (hasStringValue(newRule, "id")) {
    yamlrule.id = newRule["id"];
  }
  if (hasStringValue(newRule, "message")) {
    yamlrule.message = newRule["message"];
  } else {
    yamlrule.message = "";
  }
  if (hasStringValue(newRule, "fix")) {
    yamlrule.fix = newRule["fix"];
  } else {
    yamlrule.fix = "";
  }
  if (
    newRule["severity"] === "INFO" ||
    newRule["severity"] === "WARNING" ||
    newRule["severity"] === "ERROR"
  ) {
    yamlrule.severity = newRule["severity"];
  }
  if (hasObjectValue(newRule, "languages")) {
    // normalize for example 'typescript' into 'ts'
    yamlrule.languages = newRule["languages"].map((l: string) => ID_BY_KEY[l]);
  }
  if (hasObjectValue(newRule, "metadata")) {
    yamlrule.metadata = newRule.metadata;
  } else {
    yamlrule.metadata = {};
  }
  return yamlrule;
}

function parsePattern(
  yamlrule: SimpleRule,
  rule: any,
  path: PatternPath
): [SimpleRule, boolean] {
  yamlrule.patterns.push({
    pattern: rule["pattern"],
    path: [...path, "pattern"],
    type: "pattern" as ALLOWED_VISUAL_PATTERN_KEYS,
  });
  return [yamlrule, true];
}

function parsePatternEither(
  yamlrule: SimpleRule,
  rule: any,
  path: PatternPath
): [SimpleRule, boolean] {
  let lossLess = true;
  for (let j = 0; j < rule["pattern-either"].length; j++) {
    const innerdict = rule["pattern-either"][j];
    if (hasStringValue(innerdict, "pattern")) {
      pushPatternKey(yamlrule, innerdict, "pattern", [
        ...path,
        "pattern-either",
        j,
      ]);
    } else if (rule["pattern-either"] === " ") {
      yamlrule.patterns.push({
        pattern: "",
        path: [...path, "pattern-either", j],
        type: "pattern" as ALLOWED_VISUAL_PATTERN_KEYS,
      });
    } else {
      lossLess = false;
    }
  }
  return [yamlrule, lossLess];
}

function pushPatternKey(
  yamlrule: SimpleRule,
  dicto: any,
  key: string,
  path: PatternPath
) {
  yamlrule.patterns.push({
    pattern: dicto[key],
    path: [...path, key],
    type: key as ALLOWED_VISUAL_PATTERN_KEYS,
  });
}

function pushMetavarKey(yamlrule: SimpleRule, dicto: any, key: string) {
  yamlrule.patterns.push({
    type: key as ALLOWED_VISUAL_PATTERN_KEYS,
    metavariable: dicto[key]["metavariable"],
    constraint: dicto[key]["regex"],
  });
}

function parsePatterns(
  yamlrule: SimpleRule,
  rule: any,
  path: PatternPath
): [SimpleRule, boolean] {
  let patternAndCount = 0;
  let lossLess = true;
  for (let i = 0; i < rule["patterns"].length; i++) {
    const innerPath = [...path, "patterns", i];
    const dicto = rule["patterns"][i];
    if (hasObjectValue(dicto, "pattern-either")) {
      patternAndCount++;
      let lossLessTmp;
      [yamlrule, lossLessTmp] = parsePatternEither(yamlrule, dicto, innerPath);
      lossLess = lossLess && lossLessTmp;
    } else if (hasStringValue(dicto, "pattern")) {
      pushPatternKey(yamlrule, dicto, "pattern", innerPath);
      patternAndCount++;
    } else if (hasStringValue(dicto, "pattern-inside")) {
      pushPatternKey(yamlrule, dicto, "pattern-inside", innerPath);
    } else if (hasStringValue(dicto, "pattern-not")) {
      pushPatternKey(yamlrule, dicto, "pattern-not", innerPath);
    } else if (hasStringValue(dicto, "pattern-regex")) {
      pushPatternKey(yamlrule, dicto, "pattern-regex", innerPath);
    } else if (hasStringValue(dicto, "pattern-not-inside")) {
      pushPatternKey(yamlrule, dicto, "pattern-not-inside", innerPath);
    } else if (hasStringValue(dicto, "metavariable-regex")) {
      pushMetavarKey(yamlrule, dicto, "metavariable-regex");
    } else {
      lossLess = false;
    }
  }
  if (patternAndCount > 1) {
    lossLess = false;
  }
  return [yamlrule, lossLess];
}

// TODO:
/*
const genericPushPattern = (self, key) => {
  self.pushPatternKey(yamlRule, dicto, key);
  return 0;
}
const pushForKey = {
  "pattern": (self, key) => genericPushPattern(self, key) + 1,
  "pattern-either": (self, key) => { // your big blob, probably defined separately }
  "pattern-inside": genericPushPattern,
  "pattern-not": genericPushPattern,
  "pattern-not-inside": genericPushPattern,
}
...
// Inside `parsePatterns`
const keys = Object.getOwnPropertyNames(dicto);
if (keys.length !== 1) {
  lossLess = false;
} else {
  const [thisKey] = keys;
  const known = pushForKey[thisKey];
  if (known) {
    patternAndCount += known(this, thisKey);
  } else {
    lossLess = false;
  }
}
*/

/**
 * Semgrep YAML Validation
 */

export const validateSemgrepYaml = (
  contents: string
): RuleValidationError[] => {
  let validationErrors: RuleValidationError[] = [];

  const SEQ_TYPES = [YAMLType.SEQ, YAMLType.FLOW_SEQ];
  const MAP_TYPES = [YAMLType.MAP, YAMLType.FLOW_MAP];
  const STR_TYPES = [
    YAMLType.PLAIN,
    YAMLType.BLOCK_FOLDED,
    YAMLType.BLOCK_LITERAL,
    YAMLType.QUOTE_DOUBLE,
    YAMLType.QUOTE_SINGLE,
  ];

  // Parse contents into yaml document
  const doc = yaml.parseDocument(contents ?? "", {
    keepCstNodes: true,
    keepNodeTypes: true,
  });

  if (doc.contents === null) return validationErrors;

  for (const err of doc.errors) {
    const { message, name } = err;
    err.makePretty(); // Must pretty the line to reveal lineno and col.

    if (!err.linePos) continue;
    const { start, end } = err.linePos;
    validationErrors.push({
      source: "YAML",
      code: name,
      message: message,
      severity: "Error",
      startPos: { lineno: start.line, col: start.col },
      endPos: { lineno: end.line, col: end.col },
    });
  }

  // Error methods
  const pushError = (
    code: string,
    message: string,
    severity: "Info" | "Warning" | "Error",
    node: YAMLNode
  ) => {
    let start: Position = { lineno: 0, col: 0 };
    let end: Position = { lineno: 0, col: 0 };

    const linePos = node.cstNode?.rangeAsLinePos;
    if (linePos) {
      start.lineno = linePos.start.line;
      start.col = linePos.start.col;

      if (linePos.end) {
        end.lineno = linePos.end.line;
        end.col = linePos.end.col;
      } else {
        end = start;
        end.col += 1;
      }
    }

    validationErrors.push({
      source: "Semgrep",
      code: code,
      message: message,
      severity: severity,
      startPos: start, // TODO: Fix this
      endPos: end, // TODO: Fix this
    });
  };

  const pushParamMissingError = (
    node: YAMLNode,
    param: string,
    message?: string
  ) => {
    pushError(
      "ParamMissingError",
      message || `Missing required parameter "${param}"`,
      "Error",
      node
    );
  };

  const pushParamValueError = (node: YAMLNode, message?: string) => {
    pushError(
      "ParamValueError",
      message || `Unsupported param value`,
      "Error",
      node
    );
  };

  const pushParamTypeError = (node: YAMLNode, message?: string) => {
    pushError(
      "ParamTypeError",
      message || `Unsupported param type`,
      "Error",
      node
    );
  };

  const pushUnknownParamError = (node: YAMLNode, message?: string) => {
    pushError("UnknownParamError", message || `Unknown param`, "Error", node);
  };

  const pushNullParamError = (node: YAMLNode, message?: string) => {
    pushError(
      "NullParamError",
      message || "Expected non-null param key",
      "Error",
      node
    );
  };

  // Utility methods
  const hasParam = (node: YAMLMap, param: string) => {
    return findPair(node.items, param) !== undefined;
  };

  // Object Validators
  const validateOnlySupportedParams = (node: YAMLMap, supported: string[]) => {
    for (const pair of node.items) {
      if (pair.key === null) {
        pushNullParamError(node);
      } else {
        const value = pair.key.value;
        if (!supported.includes(value))
          pushUnknownParamError(pair.key, `Unknown parameter "${value}"`);
      }
    }
  };

  const validateNodeKey = (node: YAMLMap, key: string, types?: YAMLType[]) => {
    const pair = findPair(node.items, key);
    if (pair === undefined) {
      pushParamMissingError(node, key);
      return;
    } else if (pair.value === null) {
      pushParamValueError(
        pair.key,
        `Parameter "${key}" expected non-null value`
      );
      return;
    } else if (types !== undefined && !types.includes(pair.value.type)) {
      const expected = types.map((type) => {
        if (MAP_TYPES.includes(type)) {
          return "map value";
        } else if (SEQ_TYPES.includes(type)) {
          return "array value";
        }
        return "non-object value";
      });
      const expectedStr = [...new Set(expected)].join(", ");
      const pairValue = STR_TYPES.includes(pair.value.type)
        ? typeof pair.value.value
        : MAP_TYPES.includes(pair.value.type)
        ? "object"
        : SEQ_TYPES.includes(pair.value.type)
        ? "array"
        : "undefined";
      pushParamValueError(
        pair.value,
        `Parameter "${key}" expected ${expectedStr} but received ${pairValue} value`
      );
      return;
    }
    return pair;
  };

  const validateNodeHasKeyWithType = (
    node: YAMLMap,
    key: string,
    type: string
  ) => {
    const pair = validateNodeKey(node, key, STR_TYPES);
    if (pair !== undefined) {
      const trueType = typeof pair.value.value;
      if (trueType !== type) {
        pushParamTypeError(
          pair.value,
          `Parameter "${key}" expected ${type} but received ${trueType}`
        );
        return;
      }
    }
    return pair;
  };

  const validateNodeIsEnumerated = (
    node: YAMLMap,
    key: string,
    values: string[]
  ) => {
    const pair = validateNodeHasKeyWithType(node, key, "string");
    if (pair !== undefined) {
      if (!values.includes(pair.value.value)) {
        pushParamValueError(
          pair.value,
          `Parameter "${key}" expected one of ${values.join(
            ", "
          )} but received "${pair.value.value}"`
        );
        return;
      }
    }
    return pair;
  };

  const validateNodeIsArray = (
    node: YAMLMap,
    key: string,
    isValid?: (value: string) => boolean,
    type?: string
  ) => {
    const pair = validateNodeKey(node, key, SEQ_TYPES);
    if (pair !== undefined) {
      for (const val of pair.value.items) {
        if (val === null) {
          pushParamValueError(
            pair.key,
            `Parameter "${key}" expected non-null values for items`
          );
          return;
        } else if (!STR_TYPES.includes(val.type)) {
          pushParamValueError(
            val,
            `Parameter "${key}" expected non-object values for items`
          );
          continue;
        } else if (type !== undefined && typeof val.value !== type) {
          pushParamTypeError(
            val,
            `Parameter "${key}" expected ${type} values for items but received ${typeof val.value} value for item`
          );
          continue;
        } else if (isValid !== undefined && !isValid(val.value)) {
          pushParamValueError(
            val,
            `Parameter "${key}" does not support the item "${val.value}"`
          );
          continue;
        }
      }
    }
    return pair;
  };

  const validateNodeIsObjectArray = (
    node: YAMLMap,
    key: string,
    validateItem?: (value: YAMLMap) => void
  ) => {
    const pair = validateNodeKey(node, key, SEQ_TYPES);
    if (pair !== undefined) {
      for (const item of pair.value.items) {
        if (item === null) {
          pushNullParamError(
            pair.key,
            `Parameter "${key}" expected non-null values for items`
          );
          continue;
        } else if (!MAP_TYPES.includes(item.type)) {
          pushParamTypeError(
            item,
            `Parameter "${key}" expected object values for items`
          );
          continue;
        } else if (validateItem !== undefined) {
          validateItem(item);
        }
      }
    }
    return pair;
  };

  const validateNodeIsObject = (node: YAMLMap, key: string) => {
    const pair = validateNodeKey(node, key, MAP_TYPES);
    return pair;
  };

  // Rule Validators
  const validateSearchRuleContent = (node: YAMLMap) => {
    const validatePatterns = (node: YAMLMap) => {
      const pair = validateNodeIsObjectArray(node, "patterns", (item) => {
        if (hasParam(item, "patterns")) validatePatterns(item);
        else if (hasParam(item, "pattern-either")) validatePatternEither(item);
        else if (hasParam(item, "focus-metavariable"))
          validateFocusMetavariable(item);
        else if (hasParam(item, "pattern-inside")) validatePatternInside(item);
        else if (hasParam(item, "pattern-not-inside"))
          validatePatternNotInside(item);
        else if (hasParam(item, "pattern-not")) validatePatternNot(item);
        else if (hasParam(item, "pattern")) validatePattern(item);
        else if (hasParam(item, "pattern-regex")) validatePatternRegex(item);
        else if (hasParam(item, "pattern-not-regex"))
          validatePatternNotRegex(item);
        else if (hasParam(item, "pattern-where-python"))
          validatePatternWherePython(item);
        else if (hasParam(item, "metavariable-analysis"))
          validateMetavariableAnalysis(item);
        else if (hasParam(item, "metavariable-regex"))
          validateMetavariableRegex(item);
        else if (hasParam(item, "metavariable-pattern"))
          validateMetavariablePattern(item);
        else if (hasParam(item, "metavariable-comparison"))
          validateMetavariableComparison(item);
        else {
          pushParamValueError(
            item.items[0].key,
            `Parameter "patterns" received unsupported directive "${item.items[0].key.value}"`
          );
        }
      });
      return pair;
    };

    const validatePatternEither = (node: YAMLMap) => {
      const pair = validateNodeIsObjectArray(node, "pattern-either", (item) => {
        if (hasParam(item, "patterns")) validatePatterns(item);
        else if (hasParam(item, "pattern-either")) validatePatternEither(item);
        else if (hasParam(item, "pattern-inside")) validatePatternInside(item);
        else if (hasParam(item, "pattern")) validatePattern(item);
        else if (hasParam(item, "pattern-regex")) validatePatternRegex(item);
        else {
          pushParamValueError(
            item.items[0].key,
            `Parameter "pattern-either" received unsupported directive "${item.items[0].key.value}"`
          );
        }
      });
      return pair;
    };

    const validateFocusMetavariable = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(
        node,
        "focus-metavariable",
        "string"
      );
      return pair;
    };

    const validatePatternInside = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(node, "pattern-inside", "string");
      return pair;
    };

    const validatePatternNotInside = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(
        node,
        "pattern-not-inside",
        "string"
      );
      return pair;
    };

    const validatePatternNot = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(node, "pattern-not", "string");
      return pair;
    };

    const validatePattern = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(node, "pattern", "string");
      return pair;
    };

    const validatePatternRegex = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(node, "pattern-regex", "string");
      return pair;
    };

    const validatePatternNotRegex = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(
        node,
        "pattern-not-regex",
        "string"
      );
      return pair;
    };

    const validatePatternWherePython = (node: YAMLMap) => {
      const pair = validateNodeHasKeyWithType(
        node,
        "pattern-where-python",
        "string"
      );
      return pair;
    };

    const validateMetavariableAnalysis = (_: YAMLMap) => {};

    const validateMetavariableRegex = (_: YAMLMap) => {};

    const validateMetavariablePattern = (_: YAMLMap) => {};

    const validateMetavariableComparison = (_: YAMLMap) => {};

    // Validation starts here

    if (
      !(
        hasParam(node, "pattern") ||
        hasParam(node, "patterns") ||
        hasParam(node, "pattern-either") ||
        hasParam(node, "pattern-regex")
      )
    ) {
      pushParamMissingError(
        node,
        "",
        `Search mode rules require atleast one of "pattern", "patterns", "pattern-either" or "pattern-regex"`
      );
      return;
    }

    if (hasParam(node, "pattern")) validatePattern(node);
    if (hasParam(node, "patterns")) validatePatterns(node);
    if (hasParam(node, "pattern-either")) validatePatternEither(node);
    if (hasParam(node, "pattern-regex")) validatePatternRegex(node);
  };

  const validateTaintRuleContent = (node: YAMLMap) => {
    if (
      !(
        hasParam(node, "pattern-sources") ||
        hasParam(node, "pattern-sinks") ||
        hasParam(node, "pattern-propagators")
      )
    ) {
      pushParamMissingError(
        node,
        "",
        `Taint mode requires "pattern-sources", "pattern-sinks", or "pattern-propagators".`
      );
    }
    if (hasParam(node, "pattern")) {
      pushParamMissingError(
        node,
        "",
        `Do not use "pattern" in taint mode, use "pattern-sources", "pattern-sinks", or "pattern-propagators".`
      );
    }
  };

  const validateJoinRuleContent = (_: YAMLMap) => {};

  const validateRuleKeys = (node: YAMLMap) => {
    // Validate 'rules' key and extract first rule
    validateNodeIsObjectArray(node, "rules");
    const rules = findPair(node.items, "rules");
    if (rules?.value) {
      const pairValue = rules.value.items;
      if (pairValue.length > 0) {
        node = pairValue[0];
      }
      if (pairValue.length > 1) {
        for (var i = 1; i < pairValue.length; i++) {
          pushParamValueError(
            pairValue[i],
            `The "rules" key must have exactly one rule.`
          );
        }
      }
    }
    // Global rule validation
    validateOnlySupportedParams(node, [
      "id",
      "version",
      "message",
      "mode",
      "languages",
      "paths",
      "severity",
      "pattern-sinks",
      "pattern-sources",
      "pattern-propagators",
      "pattern-sanitizers",
      "join",
      "fix",
      "fix-regex",
      "metadata",
      "options",
      "pattern",
      "pattern-regex",
      "patterns",
      "pattern-either",
      "r2c-internal-project-depends-on",
    ]);

    // Validate `id`
    validateNodeHasKeyWithType(node, "id", "string");
    const idPair = findPair(node.items, "id");
    if (idPair?.value.value) {
      const pairValue = idPair?.value;
      if (!ID_REGEX.test(pairValue.value)) {
        pushParamValueError(
          pairValue,
          `Parameter "id" can contain only letters, numbers, underscores, dashes and periods.`
        );
      }
    }

    // Validate `message`
    validateNodeHasKeyWithType(node, "message", "string");

    // Validate `severity`
    validateNodeIsEnumerated(node, "severity", ["INFO", "WARNING", "ERROR"]);

    // Validate `languages`
    validateNodeIsArray(
      node,
      "languages",
      (value: string) => {
        return ALL_VALID_LANGUAGE_KEYS.includes(value);
      },
      "string"
    );

    // Validate `mode`, (optional)
    if (hasParam(node, "mode")) {
      const pair = validateNodeIsEnumerated(node, "mode", [
        "search",
        "taint",
        "join",
      ]);
      if (pair !== undefined) {
        const value = pair.value.value;
        if (value === "taint") validateTaintRuleContent(node);
        else if (value === "join") validateJoinRuleContent(node);
        else if (value === "search") validateSearchRuleContent(node);
      }
    } else {
      validateSearchRuleContent(node);
    }

    // Validate `paths`, (optional)
    if (hasParam(node, "paths")) {
      const pathsNode = validateNodeIsObject(node, "paths");

      if (pathsNode !== undefined) {
        if (hasParam(pathsNode.value, "include")) {
          validateNodeIsArray(pathsNode.value, "include");
        }
        if (hasParam(pathsNode.value, "exclude")) {
          validateNodeIsArray(pathsNode.value, "exclude");
        }
      }
    }

    // Validate `metadata`, (optional)
    if (hasParam(node, "metadata")) validateNodeIsObject(node, "metadata");

    // Validate `options`, (optional)
    if (hasParam(node, "options")) validateNodeIsObject(node, "options");

    // Validate 'fix', (optional)
    if (hasParam(node, "fix"))
      validateNodeHasKeyWithType(node, "fix", "string");

    // Validate `fix-regex`, (optional)
    if (hasParam(node, "fix-regex")) {
      const fixNode = validateNodeIsObject(node, "fix-regex");
      if (fixNode !== undefined) {
        const fixRegex = fixNode.value;
        validateOnlySupportedParams(fixRegex, [
          "count",
          "regex",
          "replacement",
        ]);

        validateNodeHasKeyWithType(fixRegex, "regex", "string");
        validateNodeHasKeyWithType(fixRegex, "replacement", "string");
        if (hasParam(fixRegex, "count"))
          validateNodeHasKeyWithType(fixRegex, "count", "number");
      }
    }
  };

  const validateYamlAntipatterns = (ruleText: string) => {
    // Assert that the rule doesn't use ambiguous ellipsis
    const MISGUIDING_QUOTED_ELLIPSIS = /[ \t]*pattern:[ \t]*"\.\.\."[ \t]*/gm;
    const match = MISGUIDING_QUOTED_ELLIPSIS.exec(ruleText);
    if (!match?.index) return;

    const start = match.index;
    const rowNumber = ruleText.substring(0, start).split("\n").length;

    let startPos: Position = { lineno: rowNumber, col: 0 };
    let endPos: Position = { lineno: rowNumber, col: match[0].length + 1 };

    validationErrors.push({
      source: "Semgrep",
      message:
        'Please reformat the rule to remove ambiguity between "..." and ... \nWe suggest using: \n  pattern: | \n    "..."\n',
      severity: "Warning",
      startPos: startPos,
      endPos: endPos,
    });
  };

  // Validate anti-patterns that are relevant before the YAML is parsed (typos/ambiguity)
  validateYamlAntipatterns(contents);

  // DO VALIDATION! WOOOT
  if (
    doc.contents.type &&
    (doc.contents.type === YAMLType.MAP ||
      doc.contents.type === YAMLType.FLOW_MAP)
  ) {
    validateRuleKeys(doc.contents);
  }

  return validationErrors;
};
